import { template as template_66d7b6e2a684438f8bc59bddaf4986d4 } from "@ember/template-compiler";
import { bannerError } from 'client-portal/utils/error-handling';
// eslint-disable-next-line no-restricted-imports -- the draft save must stay debounced in CP integration specs, where isTest() is true but isEmberTesting() is not
import { didCancel, task, timeout } from 'ember-concurrency';
import { session as draftStore } from 'client-portal/utils/persistent-hash-table';
import { getOwner } from '@ember/application';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { namespace } from 'client-portal/adapters/application';
import { on } from '@ember/modifier';
import { scrollToValidationError } from 'client-portal/utils/validate-record';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { waitForRender } from 'ember-simplepractice/utils/waiters';
import AvailabilityGrid from './availability-grid';
import Component from '@glimmer/component';
import Controls from './form/controls';
import DateOfBirth from './form/date-of-birth';
import EmailFields from './form/email-fields';
import Header from './form/header';
import InsuranceType from './form/insurance-type';
import LayoutContainer from 'client-portal/components/layout/container';
import NameFields from './form/name-fields';
import OnInsert from 'client-portal/modifiers/on-insert';
import PhoneFields from './form/phone-fields';
import ReasonForVisit from './form/reason-for-visit';
import SharedPatternForm from 'client-portal/components/shared/pattern-form';
import Success from './success';
import WaitlistIntakeForm, { ALL_DAY_SLOT, IS_RECAPTCHA_DISABLED } from 'client-portal/models/waitlist-intake-form';
import moment from 'moment-timezone';
import styles from './form.module.scss';
const AUTOSAVE_KEY = 'waitlist-intake-draft';
function nextSlotsFor(currentSlots, slot, checked) {
    let slots = [
        ...(currentSlots || [])
    ];
    if (!checked) {
        return slots.filter((item)=>item !== slot);
    }
    if (slot === ALL_DAY_SLOT) {
        return [
            ALL_DAY_SLOT
        ];
    }
    slots = slots.filter((item)=>item !== ALL_DAY_SLOT);
    return slots.includes(slot) ? slots : [
        ...slots,
        slot
    ];
}
export default class SiteWaitlistIntakeForm extends Component {
    @service
    store;
    @service
    analytics;
    @service
    clientTimezone;
    @service
    intl;
    @service
    recaptcha;
    styles = styles;
    @tracked
    form = null;
    @tracked
    availability = null;
    @tracked
    isComplete = false;
    constructor(owner, args){
        super(owner, args);
        let form = WaitlistIntakeForm.create(getOwner(this).ownerInjection(), {
            intl: this.intl
        });
        form.restore(this.loadDraft());
        this.form = form;
        this.availability = form.availability;
    }
    get isAutosaveDisplayed() {
        return Boolean(this.autosaveTask.lastSuccessful) && !this.isComplete;
    }
    isRecaptchaDisabled = IS_RECAPTCHA_DISABLED;
    setupAutosave = ()=>{
        if (Object.keys(this.loadDraft()).length) {
            this.autosaveTask.perform({
                immediate: true
            });
        }
    };
    updateField = (path, value)=>{
        this.form.set(path, value);
        this.autosaveTask.perform();
    };
    toggleAvailability = (day, slot, checked)=>{
        let availability = {
            ...this.availability,
            [day]: nextSlotsFor(this.availability?.[day], slot, checked)
        };
        this.availability = availability;
        this.form.set('availability', availability);
        this.autosaveTask.perform();
    };
    submit = async (event)=>{
        event?.preventDefault?.();
        if (!this.isRecaptchaDisabled) {
            this.form.set('recaptchaResponse', this.recaptcha.getResponse());
        }
        if (this.form.validations.isInvalid) {
            this.form.set('isDirty', true);
            await waitForRender();
            scrollToValidationError('[data-test-waitlist-intake-form]');
            return;
        }
        try {
            await this.submitTask.perform();
        } catch (err) {
            if (!didCancel(err)) {
                throw err;
            }
        }
    };
    @task(function*({ immediate } = {}) {
        if (!immediate) {
            yield timeout(isEmberTesting() ? 0 : 1000);
        }
        let payload = this.form.serialize();
        draftStore.set(AUTOSAVE_KEY, JSON.stringify({
            ...payload,
            _timestamp: Date.now()
        }));
        return moment.tz(this.clientTimezone.timezone);
    }).restartable()
    autosaveTask;
    // Plain JSON POST — same pattern as contact-request (no auth, not JSON:API).
    // 201 is success including duplicate-email PHI-hiding; do not special-case duplicates.
    @task(function*() {
        let payload = this.form.serializeForApi();
        try {
            let adapter = this.store.adapterFor('application');
            yield adapter.ajax(`/${namespace}/waitlist-intakes`, 'POST', {
                data: {
                    waitlistIntake: payload
                }
            });
            this.analytics.trackAnonymously('Client Added To Waitlist', {
                object: 'Client',
                action: 'Added To Waitlist'
            });
            this.autosaveTask.cancelAll();
            draftStore.remove(AUTOSAVE_KEY);
            this.isComplete = true;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } catch (err) {
            let title = err?.errors?.[0]?.title;
            if (typeof title === 'string' && title.includes('reCAPTCHA verification failed')) {
                yield this.recaptcha.reset();
            }
            bannerError(err, {
                title: this.intl.t('messages.error.waitlistRequestNotSubmitted')
            });
        }
    }).drop()
    submitTask;
    loadDraft() {
        try {
            let data = JSON.parse(draftStore.get(AUTOSAVE_KEY));
            if (data && typeof data === 'object') {
                // eslint-disable-next-line no-unused-vars
                let { _timestamp, ...draft } = data;
                return draft;
            }
        } catch (err) {
        // ignore corrupt drafts
        }
        return {};
    }
    static{
        template_66d7b6e2a684438f8bc59bddaf4986d4(`
    <div class={{this.styles.component}} {{OnInsert this.setupAutosave}}>
      <LayoutContainer class="large">
        <Header
          @isComplete={{this.isComplete}}
          @isAutosaveDisplayed={{this.isAutosaveDisplayed}}
          @autosaveTask={{this.autosaveTask}}
        />

        {{#if this.isComplete}}
          <Success data-test-waitlist-intake-complete />
        {{else}}
          <form {{on "submit" this.submit}} data-test-waitlist-intake-form>
            <SharedPatternForm class="form-container">
              <NameFields @form={{this.form}} @onUpdate={{this.updateField}} />
              <DateOfBirth @form={{this.form}} @onUpdate={{this.updateField}} />
              <InsuranceType @form={{this.form}} @onUpdate={{this.updateField}} />
              <EmailFields @form={{this.form}} @onUpdate={{this.updateField}} />
              <PhoneFields @form={{this.form}} @onUpdate={{this.updateField}} />
              <ReasonForVisit @form={{this.form}} @onUpdate={{this.updateField}} />

              <div class="row">
                <div class="col-12">
                  <AvailabilityGrid
                    @form={{this.form}}
                    @availability={{this.availability}}
                    @onToggle={{this.toggleAvailability}}
                  />
                </div>
              </div>
            </SharedPatternForm>

            <hr />

            <Controls
              @form={{this.form}}
              @isRecaptchaDisabled={{this.isRecaptchaDisabled}}
              @isSubmitting={{this.submitTask.isRunning}}
            />
          </form>
        {{/if}}
      </LayoutContainer>
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
